'use client'

import { imageQuality } from '@repo/sanity/image'
import { ProductMedia } from '@repo/sanity/shorthand'
import { cn } from '@repo/ui/lib/utils'
import { motion, useMotionValueEvent, useScroll } from 'motion/react'
import { Image, ImageProps } from 'next-sanity/image'
import { CSSProperties, FunctionComponent, useEffect, useRef, useState } from 'react'
import { useProduct, useUpdateURL } from '../../../providers/ProductProvider'
import { PageDto } from '../../../types/page.types'

interface Props {
  pageDto: PageDto
  media: ProductMedia[]
  limit?: number
  className?: string
}

const ProductThumbnails: FunctionComponent<Props> = ({ pageDto, media, limit = 3, className }) => {
  const { scrollY } = useScroll()
  const [hidden, setHidden] = useState(false)
  const [prevScroll, setPrevScroll] = useState(0)
  const updateURL = useUpdateURL()
  const { state, galleryRef, updateLightboxMedia } = useProduct()
  const mediaIndex = state.media ? parseInt(state.media) : undefined
  const scrollContainerRef = useRef<HTMLUListElement>(null)
  const count = (media || []).length

  const updateHiddenValue = (latest: number, prev: number): void => {
    const galleryHeight = galleryRef?.current?.offsetHeight || 10000
    const thumbnailsHeight = scrollContainerRef?.current?.offsetHeight || 200
    const thumbnailsTopOffset = scrollContainerRef?.current?.getBoundingClientRect().top || 1000

    if (latest + thumbnailsTopOffset + thumbnailsHeight > galleryHeight) {
      // Hide when below image gallery
      setHidden(true)
    } else {
      // Hide when scrolling down
      setHidden(prev > 0 ? latest > prev : false)
    }
  }

  useEffect(() => {
    if (mediaIndex && scrollContainerRef.current && mediaIndex <= count - 2) {
      const xOffset = mediaIndex * (24 + 1)

      scrollContainerRef.current.scrollTo(xOffset, 0)
    }
  }, [count, mediaIndex])

  useMotionValueEvent(scrollY, 'change', (latest: number) => {
    updateHiddenValue(latest, prevScroll)
    setPrevScroll(latest)
  })

  if (count === 0) {
    return null
  }

  const baseImageProps: Partial<ImageProps> = {
    fill: true,
    priority: false,
    quality: imageQuality,
    className: 'object-cover',
    sizes: '1.5rem',
  }

  return (
    <motion.nav
      initial='visible'
      variants={{
        visible: { transform: 'translate3d(0, 0, 0)' },
        hidden: { transform: 'translate3d(-110%, 0, 0)' },
      }}
      transition={{
        duration: 0.2,
        ease: 'easeOut',
      }}
      inert={hidden}
      animate={hidden ? 'hidden' : 'visible'}
      className={cn('absolute bottom-1.5 left-2 flex items-stretch lg:fixed', className)}
    >
      <ul
        ref={scrollContainerRef}
        style={{ '--width': '1.5rem', '--limit': limit } as CSSProperties}
        className='scrollbar-hide flex max-w-[calc((var(--limit)_*_var(--width))_+_((var(--limit)-1)_*_1px))] touch-none snap-x snap-mandatory items-stretch gap-px overflow-x-auto overflow-y-hidden scroll-smooth py-1 pr-1 lg:max-w-none lg:flex-col lg:overflow-auto'
      >
        {media?.map((item, i) => {
          const isVideo = 'videos' in item
          const videoMobile: any = isVideo && 'videos' in item ? item?.videos?.[0] : undefined
          const videoDesktop: any = isVideo && 'videos' in item ? item?.videos?.[1] : undefined

          return (
            <li key={i}>
              <button
                aria-label={pageDto.dictionary.lightbox?.view || 'View'}
                formAction={() => {
                  const newState = updateLightboxMedia(i.toString())

                  updateURL(newState)
                }}
                className={cn(
                  'ring-offset-(--color-background) focus-visible:outline-hidden focus-visible:ring-ring w-(--width) relative box-border flex aspect-[2/3] snap-start snap-always overflow-hidden rounded-md border border-solid border-transparent transition-colors ease-out hover:border-current focus-visible:ring-1 focus-visible:ring-offset-1',
                )}
              >
                {isVideo && videoMobile && (
                  <Image
                    {...baseImageProps}
                    alt={videoMobile?.videoPoster?.alt}
                    src={videoMobile?.videoPoster?.asset?.url}
                    className={cn(baseImageProps.className, videoDesktop && 'lg:hidden')}
                    blurDataURL={videoMobile?.videoPoster?.asset?.metadata?.lqip ?? undefined}
                    placeholder={videoMobile?.videoPoster?.asset?.metadata?.lqip ? 'blur' : undefined}
                  />
                )}
                {isVideo && videoDesktop && (
                  <Image
                    {...baseImageProps}
                    alt={videoDesktop?.videoPoster?.alt}
                    src={videoDesktop?.videoPoster?.asset?.url}
                    className={cn(baseImageProps.className, 'hidden lg:block')}
                    blurDataURL={videoDesktop?.videoPoster?.asset?.metadata?.lqip ?? undefined}
                    placeholder={videoDesktop?.videoPoster?.asset?.metadata?.lqip ? 'blur' : undefined}
                  />
                )}
                {!isVideo && (
                  <Image
                    {...baseImageProps}
                    alt={item.alt || 'Miista'}
                    src={item.asset?.url || ''}
                    blurDataURL={item.asset?.metadata?.lqip ?? undefined}
                    placeholder={item.asset?.metadata?.lqip ? 'blur' : undefined}
                  />
                )}
              </button>
            </li>
          )
        })}
      </ul>
      {limit && count > limit && <div className='ml-1 flex items-center justify-center lg:hidden'>+ {count - limit}</div>}
    </motion.nav>
  )
}

export default ProductThumbnails
